import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CareerSetup, CareerSetupParams } from '../../interfaces/career-setup.interface';
import { ApiResponse } from '../../interfaces/ApiResponse';
import { PageResponse } from '../../interfaces/page-response.interface';
import { environment } from '../../../../environments/environment.dev';
import { convertToHttpParams, CommonSearchParam } from '../../interfaces/common-search-param.interface';
import { CareerSetupService } from './career-setup.service';

@Injectable({
  providedIn: 'root'
})
export class CareerSetupApiService extends CareerSetupService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}v1/setup-data/career-setup`;

  private getCategoryUrl(category?: string): string {
    if (!category) return this.baseUrl;
    
    category = category.toUpperCase();
    
    // Assessments and Stages have different base paths
    if (category === 'ROUND_TYPE') {
      return `${environment.apiBaseUrl}v1/assessment-setups`;
    }
    if (category === 'STAGE') {
      return `${environment.apiBaseUrl}v1/setup-data/stage-setup`;
    }

    const mapping: Record<string, string> = {
      'INDUSTRY': 'industries',
      'ROLE': 'roles',
      'SKILL': 'skills'
    };

    const segment = mapping[category];
    return segment ? `${this.baseUrl}/${segment}` : this.baseUrl;
  }

  private filterParams(params: any): any {
    if (!params) return {};
    const filtered = { ...params };
    // If it's a category-specific endpoint, we don't need the category param
    if (this.getCategoryUrl(params.category) !== this.baseUrl) {
      delete filtered.category;
    }
    return filtered;
  }

  private mapPayload(data: any, category?: string): any {
    const payload: any = {
      name: data.name,
      description: data.description,
      active: data.active ?? data.isActive ?? true
    };

    // For Roles and Skills, the parent is industryUniqueId and roleUniqueId respectively
    if (category === 'ROLE') {
      payload.industryUniqueId = data.parentUniqueId || data.industryUniqueId || data.roleUniqueId;
    } else if (category === 'SKILL') {
      // Special payload for multi-skills
      return {
        roleUniqueId: data.parentUniqueId || data.roleUniqueId || data.industryUniqueId,
        skills: data.skills || [],
        description: data.description,
        active: data.active ?? data.isActive ?? true
      };
    } else if (category === 'ROUND_TYPE') {
      // Assessments need full structured fields
      return {
        ...data,
        ...payload,
        roleUniqueId: data.parentUniqueId || data.roleUniqueId || data.industryUniqueId
      };
    } else if (category === 'STAGE') {
      payload.roleUniqueId = data.parentUniqueId || data.roleUniqueId || data.industryUniqueId;
    }
    // For other non-industry categories, we might need parentUniqueId
    else if (category !== 'INDUSTRY' && (data.parentUniqueId || data.roleUniqueId)) {
      payload.parentUniqueId = data.parentUniqueId || data.roleUniqueId;
    }
    
    return payload;
  }

  constructor() {
    super();
  }

  getCareerSetup(params: CareerSetupParams): Observable<CareerSetup[]> {
    const url = this.getCategoryUrl(params.category);
    const filteredParams = this.filterParams(params);
    const httpParams = convertToHttpParams(filteredParams);
    
    return this.wrapRequest(
      this.http.get<ApiResponse<any>>(url, {
        headers: this.headers,
        params: httpParams
      }).pipe(
        map(res => {
          const data = res?.response;
          if (Array.isArray(data)) return data;
          if (data && Array.isArray(data.content)) return data.content;
          return [];
        })
      ),
      false,
      false
    );
  }

  override findAll(params: any): Observable<PageResponse<CareerSetup>> {
    const url = this.getCategoryUrl(params.category);
    const filteredParams = this.filterParams(params);
    const httpParams = convertToHttpParams(filteredParams);
    
    return this.wrapRequest(
      this.http.get<ApiResponse<PageResponse<CareerSetup>>>(url, {
        headers: this.headers,
        params: httpParams
      }).pipe(
        map(res => res?.response || { content: [], page: { number: 0, size: 0, totalElements: 0, totalPages: 0 } })
      ),
      false,
      false
    );
  }

  override find(id: string, category?: string): Observable<CareerSetup> {
    const url = this.getCategoryUrl(category);
    return this.wrapRequest(
      this.http.get<ApiResponse<CareerSetup>>(`${url}/${id}`, { headers: this.headers }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override create(data: Partial<CareerSetup>): Observable<CareerSetup> {
    const category = data.category;
    const url = this.getCategoryUrl(category);
    const payload = this.mapPayload(data, category);
    
    return this.wrapRequest(
      this.http.post<ApiResponse<CareerSetup>>(url, payload, { headers: this.headers }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override update(id: string, data: Partial<CareerSetup>): Observable<CareerSetup> {
    const category = data.category;
    const url = this.getCategoryUrl(category);
    const payload = this.mapPayload(data, category);

    return this.wrapRequest(
      this.http.put<ApiResponse<CareerSetup>>(`${url}/${id}`, payload, { headers: this.headers }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  override delete(id: string, category?: string): Observable<void> {
    const url = this.getCategoryUrl(category);
    return this.wrapRequest(
      this.http.delete<ApiResponse<void>>(`${url}/${id}`, { headers: this.headers }).pipe(
        map(() => undefined)
      ),
      false,
      false
    );
  }

  override updateStatus(id: string, isActive: boolean, category?: string): Observable<CareerSetup> {
    const url = this.getCategoryUrl(category);
    return this.wrapRequest(
      this.http.patch<ApiResponse<CareerSetup>>(`${url}/${id}/status`, null, {
        headers: this.headers,
        params: { isActive: isActive.toString() }
      }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }

  getHierarchy(industryId: string): Observable<any> {
    return this.wrapRequest(
      this.http.get<ApiResponse<any>>(`${this.baseUrl}/hierarchy/${industryId}`, { headers: this.headers }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }
  
  getConfigured(industryId: string): Observable<any> {
    return this.wrapRequest(
      this.http.get<ApiResponse<any>>(`${this.baseUrl}/configured/${industryId}`, { headers: this.headers }).pipe(
        map(res => res?.response)
      ),
      false,
      false
    );
  }
}
